import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Paper,
  CircularProgress,
  Alert,
  InputAdornment,
  Chip,
  Button,
  Collapse,
  IconButton,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  Business as BusinessIcon,
  Public as PublicIcon,
  ViewList as ViewListIcon,
  UploadFile as UploadFileIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import CaseStudyCard from '../components/CaseStudyCard';
import PatternStrength from '../components/PatternStrength';
import { getVisibleCaseStudies, getDocumentTexts } from '../../../services/documentService';
import { runFullAnalysis } from '../../../services/patternLibraryService';
import { useAuth } from '../../../contexts/AuthContext';
import { REPS_COLORS } from '../utils/constants';
import { useUserOrganizations } from '../hooks/useOrganization';
import { includesIndustryLibrary } from '../utils/libraryPrefs';
import OrgSwitcher from '../components/OrgSwitcher';
import { parseQuery, searchCaseStudies } from '../utils/textSearch.js';

/** Industry options for filter dropdown */
const INDUSTRY_OPTIONS = [
  { value: '', label: 'All Industries' },
  { value: 'oil_gas', label: 'Oil & Gas' },
  { value: 'chemical', label: 'Chemical' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'nuclear', label: 'Nuclear' },
  { value: 'mining', label: 'Mining' },
  { value: 'construction', label: 'Construction' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'transportation', label: 'Transportation' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'other', label: 'Other' },
];

/** Source type options for filter dropdown */
const SOURCE_OPTIONS = [
  { value: '', label: 'All Sources' },
  { value: 'csb', label: 'CSB Report' },
  { value: 'internal', label: 'Internal' },
  { value: 'audit', label: 'Audit' },
  { value: 'observation', label: 'Observation' },
  { value: 'regulatory', label: 'Regulatory' },
  { value: 'other', label: 'Other' },
];

/** REPS quadrant options for checkbox filters */
const REPS_OPTIONS = [
  { key: 'reliability', label: 'Reliability', color: REPS_COLORS.reliability },
  { key: 'efficiency', label: 'Efficiency', color: REPS_COLORS.efficiency },
  { key: 'productivity', label: 'Productivity', color: REPS_COLORS.productivity },
  { key: 'safety', label: 'Safety', color: REPS_COLORS.safety },
];

/** Scope options for the toggle */
const SCOPE_ALL = 'all';
const SCOPE_MY_ORG = 'my_org';
const SCOPE_INDUSTRY = 'industry';

const SUPERADMIN_EMAILS = [
  'alex.samaniego@ppiweb.com',
  'tim.autrey@ppiweb.com',
];

export default function CaseStudies() {
  const { user } = useAuth();
  // P5-16: same org resolution as every other page (OrgSwitcher / useUserOrganizations)
  const { organizations } = useUserOrganizations(user?.uid);
  const navigate = useNavigate();
  const isSuperAdmin = user && SUPERADMIN_EMAILS.includes(user.email);
  const [showPatterns, setShowPatterns] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);

  const userOrgId = organizations[0]?.id || null;
  const industryHidden = !includesIndustryLibrary(organizations[0]); // Learning Library switch (Aug 25)

  // Data state
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter state
  const [scopeFilter, setScopeFilter] = useState(SCOPE_ALL);
  const [searchQuery, setSearchQuery] = useState('');
  const [industryFilter, setIndustryFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [repsFilters, setRepsFilters] = useState({
    reliability: false,
    efficiency: false,
    productivity: false,
    safety: false,
  });
  const [showFilters, setShowFilters] = useState(true);

  // Fetch case studies on mount
  useEffect(() => {
    let cancelled = false;

    async function fetchData() {
      setLoading(true);
      setError(null);
      try {
        // Shared library + this organization's own studies (P7-11 privacy fence); no status
        // filter so analyzed-but-not-yet-published case studies from the n8n pipeline show up too.
        const all = await getVisibleCaseStudies(userOrgId);
        // Show 'ready', 'published', and 'analyzing' (in-progress) — hide 'error' and 'failed'
        const visible = all.filter((cs) =>
          ['ready', 'published', 'analyzing'].includes(cs.status) || !cs.status
        );
        if (!cancelled) {
          setCaseStudies(visible);
        }
      } catch (err) {
        console.error('Failed to fetch case studies:', err);
        if (!cancelled) {
          setError(err.message || 'Failed to load case studies.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchData();
    return () => { cancelled = true; };
  }, [userOrgId]);

  // P5-08: full-text search. Once the query has real terms, load the extracted text
  // (Hugo_DocumentText) for the visible case studies — once per page visit, then cached.
  const terms = useMemo(() => parseQuery(searchQuery), [searchQuery]);
  const [fullTextById, setFullTextById] = useState({});
  const [textLoading, setTextLoading] = useState(false);
  const textLoadedRef = useRef(false);
  const mountedRef = useRef(true);
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []); // StrictMode-safe
  useEffect(() => {
    if (!terms.length || textLoadedRef.current || !caseStudies.length) return;
    textLoadedRef.current = true; // one fetch per page visit; later keystrokes reuse the cache
    const docIdOf = (cs) => cs.sourceDocumentId || (typeof cs.id === 'string' && cs.id.startsWith('cs_') ? cs.id.slice(3) : null);
    setTextLoading(true);
    getDocumentTexts(caseStudies.map(docIdOf).filter(Boolean))
      .then((byDocId) => {
        if (!mountedRef.current) return;
        const byCase = {};
        for (const cs of caseStudies) {
          const docId = docIdOf(cs);
          if (docId && byDocId[docId]) byCase[cs.id] = byDocId[docId];
        }
        setFullTextById(byCase);
      })
      .catch((err) => console.warn('Full-text index unavailable:', err?.message || err))
      .finally(() => { if (mountedRef.current) setTextLoading(false); });
  }, [terms, caseStudies]);

  // Handle REPS checkbox change
  const handleRepsChange = (key) => {
    setRepsFilters((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Check if any filters are active
  const hasActiveFilters = searchQuery || industryFilter || sourceFilter ||
    scopeFilter !== SCOPE_ALL || Object.values(repsFilters).some(Boolean);

  // Clear all filters
  const clearFilters = () => {
    setScopeFilter(SCOPE_ALL);
    setSearchQuery('');
    setIndustryFilter('');
    setSourceFilter('');
    setRepsFilters({
      reliability: false,
      efficiency: false,
      productivity: false,
      safety: false,
    });
  };

  // Active REPS filter keys
  const activeRepsKeys = useMemo(
    () => Object.entries(repsFilters).filter(([, v]) => v).map(([k]) => k),
    [repsFilters]
  );

  // Client-side filtering
  const filteredStudies = useMemo(() => {
    // P5-08: text search over title / summary / findings / extracted document text
    const hits = searchCaseStudies(caseStudies, searchQuery, fullTextById);
    const hitById = {};
    for (const h of hits) hitById[h.caseStudy.id] = h;
    return caseStudies.filter((cs) => {
      if (terms.length && !hitById[cs.id]) return false;
      // Scope filter: My Organization vs Industry Library
      if (scopeFilter === SCOPE_MY_ORG) {
        if (!userOrgId || cs.organizationId !== userOrgId) return false;
      } else if (scopeFilter === SCOPE_INDUSTRY) {
        // Industry Library: show global/non-org-specific case studies
        if (cs.organizationId && cs.organizationId === userOrgId) return false;
      }

      // Industry filter
      if (industryFilter && cs.industry !== industryFilter) return false;

      // Source filter
      if (sourceFilter && cs.source !== sourceFilter) return false;

      // REPS checkbox filter: show case studies that have a score > 0 for checked quadrants
      if (activeRepsKeys.length > 0) {
        const mapping = cs.repsMapping || {};
        const hasMatchingReps = activeRepsKeys.some(
          (key) => mapping[key] != null && mapping[key] > 0
        );
        if (!hasMatchingReps) return false;
      }

      return true;
    }).map((cs) => ({ ...cs, __hit: hitById[cs.id] || null }));
  }, [caseStudies, scopeFilter, userOrgId, industryFilter, sourceFilter, activeRepsKeys, searchQuery, terms, fullTextById]);

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flexGrow: 1, minWidth: 280 }}>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            REPSit Learning Library
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            REPSit.ai and each of your Intelligent Assistants learn from the information uploaded
            to its Learning Library. A selection of Case Studies from the US Chemical Safety Board
            (csb.gov) has been pre-loaded to help REPSit.ai understand and assess Organizational
            Entropy (Drift &amp; Accumulation) using real scenarios and real data.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Organization and Industry-specific reports such as Regulatory Reports, Causal Analyses,
            and Audit &amp; Observation Reports can be uploaded to this library as well.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'stretch', md: 'flex-end' }, gap: 1, flexShrink: 0, minWidth: 0, width: { xs: '100%', md: 'auto' } }}>
          <OrgSwitcher sx={{ maxWidth: '100%', width: { xs: '100%', md: 'auto' } }} />
          <Button
            variant="contained"
            startIcon={<UploadFileIcon />}
            onClick={() => navigate('/drew/documents')}
            sx={{ whiteSpace: 'nowrap', alignSelf: { xs: 'flex-start', md: 'auto' } }}
          >
            Document Upload Portal
          </Button>
        </Box>
      </Box>

      {/* Superadmin: Pattern Analysis Controls */}
      {isSuperAdmin && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'action.hover' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant={showPatterns ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setShowPatterns(!showPatterns)}
            >
              {showPatterns ? 'Hide Pattern Library' : 'View Pattern Library'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              disabled={analyzing}
              onClick={async () => {
                setAnalyzing(true);
                setAnalysisResult(null);
                try {
                  const result = await runFullAnalysis();
                  setAnalysisResult(result);
                } catch (err) {
                  setAnalysisResult({ error: err.message });
                } finally {
                  setAnalyzing(false);
                }
              }}
            >
              {analyzing ? 'Analyzing...' : 'Run Cross-Report Analysis'}
            </Button>
            {analysisResult && !analysisResult.error && (
              <Typography variant="body2" color="success.main">
                Analysis complete: {analysisResult.patternsFound} patterns from {analysisResult.caseStudiesAnalyzed} case studies
              </Typography>
            )}
            {analysisResult?.error && (
              <Typography variant="body2" color="error">
                Analysis failed: {analysisResult.error}
              </Typography>
            )}
          </Box>
        </Paper>
      )}

      {/* Pattern Library View */}
      {showPatterns && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <PatternStrength showAll />
        </Paper>
      )}

      {/* Tabs (P5-09, Tim D10): All · Industry Case Studies · Organizational Documents */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={scopeFilter} onChange={(e, val) => setScopeFilter(val)} variant="scrollable" allowScrollButtonsMobile>
          <Tab value={SCOPE_ALL} icon={<ViewListIcon fontSize="small" />} iconPosition="start" label="All" sx={{ minHeight: 44 }} />
          <Tab value={SCOPE_INDUSTRY} icon={<PublicIcon fontSize="small" />} iconPosition="start" label="Industry Case Studies" sx={{ minHeight: 44 }} />
          <Tab value={SCOPE_MY_ORG} icon={<BusinessIcon fontSize="small" />} iconPosition="start" label="Organizational Documents" disabled={!userOrgId} sx={{ minHeight: 44 }} />
        </Tabs>
        {!userOrgId && (
          <Typography variant="caption" color="text.secondary">
            Join an organization to see its internal documents
          </Typography>
        )}
      </Box>

      {/* Filter Bar */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: showFilters ? 2 : 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterListIcon color="action" />
            <Typography variant="subtitle2" fontWeight={600}>
              Filters
            </Typography>
            {hasActiveFilters && (
              <Chip
                label={`${filteredStudies.length} of ${caseStudies.length}`}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {hasActiveFilters && (
              <Button size="small" startIcon={<ClearIcon />} onClick={clearFilters}>
                Clear All
              </Button>
            )}
            <IconButton
              size="small"
              onClick={() => setShowFilters((prev) => !prev)}
              sx={{ transform: showFilters ? 'rotate(0deg)' : 'rotate(180deg)', transition: 'transform 0.2s' }}
            >
              <FilterListIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Collapse in={showFilters}>
          <Grid container spacing={2} alignItems="center">
            {/* Search */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder='Search titles, findings and full document text — e.g. "Texas City"'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                helperText={textLoading ? 'Indexing document text…' : terms.length ? `${filteredStudies.length} match${filteredStudies.length === 1 ? '' : 'es'} — matching text is highlighted` : ' '}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {textLoading ? <CircularProgress size={16} /> : <SearchIcon fontSize="small" color="action" />}
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchQuery('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Grid>

            {/* Industry Select */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Industry</InputLabel>
                <Select
                  value={industryFilter}
                  label="Industry"
                  onChange={(e) => setIndustryFilter(e.target.value)}
                >
                  {INDUSTRY_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Source Select */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Source</InputLabel>
                <Select
                  value={sourceFilter}
                  label="Source"
                  onChange={(e) => setSourceFilter(e.target.value)}
                >
                  {SOURCE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* REPS Checkboxes */}
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                Looking for Info in Specific REPS Quadrant(s)?
              </Typography>
              <FormGroup row>
                {REPS_OPTIONS.map((opt) => (
                  <FormControlLabel
                    key={opt.key}
                    control={
                      <Checkbox
                        size="small"
                        checked={repsFilters[opt.key]}
                        onChange={() => handleRepsChange(opt.key)}
                        sx={{
                          color: opt.color,
                          '&.Mui-checked': { color: opt.color },
                        }}
                      />
                    }
                    label={
                      <Typography variant="caption" fontWeight={600}>
                        {opt.label}
                      </Typography>
                    }
                  />
                ))}
              </FormGroup>
            </Grid>
          </Grid>
        </Collapse>
      </Paper>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && !loading && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Empty State */}
      {!loading && !error && filteredStudies.length === 0 && (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {caseStudies.length === 0
              ? 'No case studies yet'
              : 'No case studies match your filters'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {caseStudies.length === 0
              ? (industryHidden
                ? 'Industry case studies are switched off for your organization (Settings → Organization → Learning Library). Upload your own documents in the Document Upload Portal and they appear here.'
                : 'Upload an incident report, audit, or observation on the Documents page — the assistant analyzes it and publishes the case study here.')
              : 'Try adjusting your search or filter criteria.'}
          </Typography>
          {hasActiveFilters && (
            <Button sx={{ mt: 2 }} onClick={clearFilters} variant="outlined" size="small">
              Clear Filters
            </Button>
          )}
        </Paper>
      )}

      {/* Case Studies Grid */}
      {!loading && !error && filteredStudies.length > 0 && (
        <Grid container spacing={3}>
          {filteredStudies.map((cs) => (
            <Grid item xs={12} sm={6} lg={4} key={cs.id}>
              <Box sx={{ position: 'relative' }}>
                {/* Org badge for org-specific case studies */}
                {cs.organizationId && cs.organizationId === userOrgId && (
                  <Chip
                    icon={<BusinessIcon sx={{ fontSize: 14 }} />}
                    label="My Org"
                    size="small"
                    color="primary"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      zIndex: 1,
                      fontWeight: 600,
                      fontSize: '0.65rem',
                      height: 22,
                    }}
                  />
                )}
                <CaseStudyCard caseStudy={cs} searchQuery={terms.length ? searchQuery : ''} hit={cs.__hit} />
              </Box>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}
